
import React, { useState } from 'react';

interface DisputeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  milestoneId: number;
  milestoneDescription: string;
  loading?: boolean;
}

const DisputeModal: React.FC<DisputeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  milestoneId,
  milestoneDescription,
  loading = false,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (reason.trim().length < 10) {
      setError('Dispute reason must be at least 10 characters');
      return;
    }
    if (reason.trim().length > 500) {
      setError('Dispute reason must be at most 500 characters');
      return;
    }

    onSubmit(reason.trim());
    setReason('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#471b00] font-poly">
              Open Dispute
            </h3>
            <p className="text-sm text-[#471b007f] font-dm mt-1">
              Milestone {milestoneId + 1}: {milestoneDescription}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-[#471b007f] hover:text-[#471b00] transition-colors disabled:opacity-50"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex gap-2">
            <svg
              className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="text-sm text-red-800 font-dm">
              <p className="font-medium mb-1">Important:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Opening a dispute will lock this milestone</li>
                <li>No approvals or releases can occur until resolved</li>
                <li>An arbitrator must be assigned to resolve the dispute</li>
                <li>Other milestones will remain unaffected</li>
              </ul>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="reason"
              className="block text-sm font-medium text-[#471b00] mb-2 font-dm"
            >
              Dispute Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              rows={5}
              className="w-full px-4 py-2 border border-[#471b001e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF3333] focus:border-transparent font-dm text-sm disabled:opacity-50"
              placeholder="Describe the issue with this milestone (minimum 10 characters)..."
              required
            />
            <p className="text-xs text-[#471b007f] mt-1 font-dm">
              {reason.length} / 500 characters (min 10)
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-dm">
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-[#471b001e] text-[#471b00] rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-dm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || reason.trim().length < 10 || reason.trim().length > 500}
              className="px-4 py-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-dm"
            >
              {loading ? 'Opening Dispute...' : 'Open Dispute'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DisputeModal;
